import React from 'react';

import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { Grid } from '@mui/material';

import { Field } from '@hrlogics/react-components/form';

import { USA_STATES_OPTIONS } from '@clearverify/constants';

import { SelectField, TextField } from 'src/components/finalForm';
import { AddressFieldsFormFields } from 'src/constants/address';

import { AddressFieldsWrapper, ConditionalWrapper } from './components';

interface AddressFieldsProps {
  prefix?: string;
  addressLabel?: string;
  required?: boolean;
  requiredStreet?: boolean;
  requiredCity?: boolean;
  requiredCounty?: boolean;
  requiredState?: boolean;
  requiredZipCode?: boolean;
  disabled?: boolean;
  hasOwnGrid?: boolean;
  isCompactStateZip?: boolean;
  inputSlotProps?: MuiTextFieldProps['slotProps'];
}

export const AddressFields: React.FC<AddressFieldsProps> = ({
  prefix,
  addressLabel,
  required = false,
  requiredStreet = false,
  requiredCity = false,
  requiredCounty = false,
  requiredState = false,
  requiredZipCode = false,
  disabled = false,
  hasOwnGrid = true,
  isCompactStateZip = true,
  inputSlotProps,
}) => {
  const fieldNames = React.useMemo(() => {
    return {
      [AddressFieldsFormFields.county]: generateName(AddressFieldsFormFields.county, prefix),
      [AddressFieldsFormFields.state]: generateName(AddressFieldsFormFields.state, prefix),
      [AddressFieldsFormFields.city]: generateName(AddressFieldsFormFields.city, prefix),
      [AddressFieldsFormFields.street]: generateName(AddressFieldsFormFields.street, prefix),
      [AddressFieldsFormFields.zipCode]: generateName(AddressFieldsFormFields.zipCode, prefix),
    } as const;
  }, [prefix]);

  return (
    <ConditionalWrapper condition={hasOwnGrid} wrapper={AddressFieldsWrapper}>
      <React.Fragment>
        <Grid size={6}>
          <Field
            name={fieldNames.street}
            label={addressLabel || 'Address'}
            fullWidth
            component={TextField}
            required={required || requiredStreet}
            disabled={disabled}
            slotProps={inputSlotProps}
          />
        </Grid>
        <Grid size={6}>
          <Field
            name={fieldNames.city}
            label="City"
            fullWidth
            component={TextField}
            required={required || requiredCity}
            disabled={disabled}
            slotProps={inputSlotProps}
          />
        </Grid>
        <Grid size={6}>
          <Field
            name={fieldNames.county}
            label="County"
            fullWidth
            component={TextField}
            required={required || requiredCounty}
            disabled={disabled}
            slotProps={inputSlotProps}
          />
        </Grid>
        <Grid size={isCompactStateZip ? 3 : 6}>
          <Field
            label="State"
            name={fieldNames.state}
            component={SelectField}
            options={USA_STATES_OPTIONS}
            fullWidth
            required={required || requiredState}
            disabled={disabled}
          />
        </Grid>
        <Grid size={isCompactStateZip ? 3 : 6}>
          <Field
            name={fieldNames.zipCode}
            label="Zip Code"
            max={5}
            fullWidth
            showErrorOnlyTouched={false}
            component={TextField}
            required={required || requiredZipCode}
            disabled={disabled}
            slotProps={inputSlotProps}
          />
        </Grid>
      </React.Fragment>
    </ConditionalWrapper>
  );
};

const generateName = (name: string, prefix?: string) => {
  if (!prefix) return name;
  return [prefix, name].join('.');
};
